import { Icon } from '@iconify/react';
import { SwitchChangeEvent } from '@progress/kendo-react-inputs';
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'react-tooltip';
import MailboxIcon from 'src/assets/icons/MailboxIcon';
import Button from 'src/components/design-system/Button';
import PopUp from 'src/components/design-system/PopUp';
import Switch from 'src/components/design-system/Switch';
import LoadingPanel from 'src/components/LoadingPanel';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import { useContextSelector } from 'use-context-selector';
import EmailNotificationsConfirmDialog from './EmailNotificationsConfirmDialog';
import { ManageEmailsContext } from './ManageEmailsContext';

const NOTIFICATION_EMAIL_TOOLTIP_ID = 'notification-email-tooltip';

const notificationEmailTooltipStyle: CSSProperties = {
  backgroundColor: 'black',
  color: 'white',
  zIndex: 99999,
  minWidth: 180,
  maxWidth: 320,
  whiteSpace: 'normal',
  wordWrap: 'break-word',
  overflowWrap: 'anywhere',
};

function InfoTooltip({ messageKey }: { messageKey: string }) {
  const { t } = useTranslation();
  return (
    <span className="inline-flex items-center self-center">
      <PopUp
        content={
          <div className="m-1 max-w-[350px]">
            <PopUpContentWrapper type="Information" contents={[t(messageKey)]} />
          </div>
        }
        popupAlign={{ horizontal: 'center', vertical: 'bottom' }}
        anchorAlign={{ horizontal: 'center', vertical: 'top' }}
      >
        <span className="inline-flex items-center justify-center leading-none">
          <Icon
            icon="mdi:information"
            width={18}
            height={18}
            className="text-info cursor-pointer flex-shrink-0"
          />
        </span>
      </PopUp>
    </span>
  );
}

const SystemNotifications = () => {
  const { t } = useTranslation();
  const selectedNotificationAddress = useContextSelector(
    ManageEmailsContext,
    (c) => c.selectedNotificationAddress
  );
  const notificationToggles = useContextSelector(ManageEmailsContext, (c) => c.notificationToggles);
  const notificationVisibility = useContextSelector(
    ManageEmailsContext,
    (c) => c.notificationVisibility
  );
  const isNotificationTypesLoading = useContextSelector(
    ManageEmailsContext,
    (c) => c.isNotificationTypesLoading
  );
  const isUpdatingNotificationTypes = useContextSelector(
    ManageEmailsContext,
    (c) => c.isUpdatingNotificationTypes
  );
  const updateNotificationToggles = useContextSelector(
    ManageEmailsContext,
    (c) => c.updateNotificationToggles
  );
  const saveNotificationToggles = useContextSelector(
    ManageEmailsContext,
    (c) => c.saveNotificationToggles
  );
  const hasNotificationTogglesDirty = useContextSelector(
    ManageEmailsContext,
    (c) => c.hasNotificationTogglesDirty
  );
  const manageAddresses = useContextSelector(ManageEmailsContext, (c) => c.manageAddresses);
  const editingId = useContextSelector(ManageEmailsContext, (c) => c.editingId);
  const isAddingNewEmail = useContextSelector(ManageEmailsContext, (c) => c.isAddingNewEmail);
  const isNotificationEmailsLoading = useContextSelector(
    ManageEmailsContext,
    (c) => c.isNotificationEmailsLoading
  );
  const isUnsavedTogglesDialogOpen = useContextSelector(
    ManageEmailsContext,
    (c) => c.isUnsavedTogglesDialogOpen
  );
  const closeUnsavedTogglesDialog = useContextSelector(
    ManageEmailsContext,
    (c) => c.closeUnsavedTogglesDialog
  );
  const onSaveAndContinueNotificationAddress = useContextSelector(
    ManageEmailsContext,
    (c) => c.onSaveAndContinueNotificationAddress
  );
  const onDiscardNotificationAddressChanges = useContextSelector(
    ManageEmailsContext,
    (c) => c.onDiscardNotificationAddressChanges
  );

  const isNotificationEmailEditing = editingId !== null || isAddingNewEmail;
  const showNoNotificationEmails = !isNotificationEmailsLoading && manageAddresses.length === 0;

  const showAllNotifications =
    notificationVisibility.showPurchaseOrders ||
    notificationVisibility.showInvoices ||
    notificationVisibility.showOcr ||
    notificationVisibility.showMessages;

  return (
    <>
      <div
        className="flex min-w-0 flex-col w-full border border-gray-300"
        style={{ minHeight: '400px', boxSizing: 'border-box' }}
      >
        <div className="flex items-center justify-between gap-3 px-4 h-10 bg-[#3F6578] w-full flex-shrink-0">
          <h2 className="text-white text-sm font-medium">{t('systemNotifications')}</h2>
        </div>
        <div className="flex-1 min-h-0 bg-white overflow-auto p-4 flex flex-col gap-4">
          {!showNoNotificationEmails && (
            <div
              className="flex min-w-0 items-baseline gap-1 rounded px-3 py-2 text-sm font-bold"
              style={{ backgroundColor: '#3FA9DB1A', color: '#3FA9DB' }}
            >
              <span className="shrink-0">{t('changesApplyTo', { email: '' }).trimEnd()}</span>
              <span
                className="min-w-0 truncate"
                data-tooltip-id={NOTIFICATION_EMAIL_TOOLTIP_ID}
                data-tooltip-content={selectedNotificationAddress ?? ''}
              >
                {selectedNotificationAddress}
              </span>
            </div>
          )}

          {showNoNotificationEmails ? (
            <div className="flex flex-col items-center justify-center flex-1 min-h-[240px] px-4 text-center">
              <MailboxIcon width={56} height={56} className="opacity-70" />
              <h3 className="text-base font-bold text-gray-900 mt-4">
                {t('notificationEmailAddressNotFound')}
              </h3>
              <p className="text-sm text-gray-500 mt-2 max-w-sm whitespace-pre-line">
                {t('addNotificationEmailToReceiveSystemNotifications')}
              </p>
            </div>
          ) : isNotificationTypesLoading ? (
            <div className="relative min-h-[120px]">
              <LoadingPanel />
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {showAllNotifications && (
                <div className="flex justify-between items-center">
                  <span className="text-sm font-semibold text-gray-900">
                    {t('allNotifications')}
                  </span>
                  <Switch
                    value={notificationToggles.allNotifications}
                    onChange={(e: SwitchChangeEvent) => {
                      const value = e.value ?? false;
                      updateNotificationToggles({
                        ...notificationToggles,
                        allNotifications: value,
                        ...(notificationVisibility.showPurchaseOrders && {
                          buyerPurchaseOrders: value,
                        }),
                        ...(notificationVisibility.showOcr && { ocr: value }),
                        ...(notificationVisibility.showInvoices && { buyerInvoices: value }),
                        ...(notificationVisibility.showMessages && { messages: value }),
                      });
                    }}
                    size="small"
                    className={'!w-[54px]'}
                    disabled={isUpdatingNotificationTypes}
                  />
                </div>
              )}

              {notificationVisibility.showPurchaseOrders && (
                <div className="flex justify-between items-center gap-2">
                  <span className="text-sm font-semibold text-gray-900 flex items-center gap-1.5 leading-none">
                    {t('purchaseOrders')}
                    <InfoTooltip messageKey="purchaseOrdersTooltip" />
                  </span>
                  <Switch
                    value={notificationToggles.buyerPurchaseOrders}
                    onChange={(e: SwitchChangeEvent) => {
                      const newVal = e.value ?? false;
                      const updated = { ...notificationToggles, buyerPurchaseOrders: newVal };
                      const allOn =
                        (!notificationVisibility.showPurchaseOrders ||
                          updated.buyerPurchaseOrders) &&
                        (!notificationVisibility.showInvoices || updated.buyerInvoices) &&
                        (!notificationVisibility.showOcr || updated.ocr) &&
                        (!notificationVisibility.showMessages || updated.messages);
                      updateNotificationToggles({ ...updated, allNotifications: allOn });
                    }}
                    size="small"
                    className={'!w-[54px]'}
                    disabled={isUpdatingNotificationTypes}
                  />
                </div>
              )}

              {notificationVisibility.showInvoices && (
                <div className="flex justify-between items-center gap-2">
                  <span className="text-sm font-semibold text-gray-900 flex items-center gap-1.5 leading-none">
                    {t('invoices')}
                    <InfoTooltip messageKey="invoicesTooltip" />
                  </span>
                  <Switch
                    value={notificationToggles.buyerInvoices}
                    onChange={(e: SwitchChangeEvent) => {
                      const newVal = e.value ?? false;
                      const updated = { ...notificationToggles, buyerInvoices: newVal };
                      const allOn =
                        (!notificationVisibility.showPurchaseOrders ||
                          updated.buyerPurchaseOrders) &&
                        (!notificationVisibility.showInvoices || updated.buyerInvoices) &&
                        (!notificationVisibility.showOcr || updated.ocr) &&
                        (!notificationVisibility.showMessages || updated.messages);
                      updateNotificationToggles({ ...updated, allNotifications: allOn });
                    }}
                    size="small"
                    className={'!w-[54px]'}
                    disabled={isUpdatingNotificationTypes}
                  />
                </div>
              )}

              {notificationVisibility.showOcr && (
                <div className="flex justify-between items-center gap-2">
                  <span className="text-sm font-semibold text-gray-900 flex items-center gap-1.5 leading-none">
                    {t('ocr')}
                    <InfoTooltip messageKey="ocrTooltip" />
                  </span>
                  <Switch
                    value={notificationToggles.ocr}
                    onChange={(e: SwitchChangeEvent) => {
                      const newVal = e.value ?? false;
                      const updated = { ...notificationToggles, ocr: newVal };
                      const allOn =
                        (!notificationVisibility.showPurchaseOrders ||
                          updated.buyerPurchaseOrders) &&
                        (!notificationVisibility.showInvoices || updated.buyerInvoices) &&
                        (!notificationVisibility.showOcr || updated.ocr) &&
                        (!notificationVisibility.showMessages || updated.messages);
                      updateNotificationToggles({ ...updated, allNotifications: allOn });
                    }}
                    size="small"
                    className={'!w-[54px]'}
                    disabled={isUpdatingNotificationTypes}
                  />
                </div>
              )}

              {notificationVisibility.showMessages && (
                <div className="flex justify-between items-center gap-2">
                  <span className="text-sm font-semibold text-gray-900 flex items-center gap-1.5 leading-none">
                    {t('internalMessages')}
                    <InfoTooltip messageKey="internalMessagesTooltip" />
                  </span>
                  <Switch
                    value={notificationToggles.messages}
                    onChange={(e: SwitchChangeEvent) => {
                      const newVal = e.value ?? false;
                      const updated = { ...notificationToggles, messages: newVal };
                      const allOn =
                        (!notificationVisibility.showPurchaseOrders ||
                          updated.buyerPurchaseOrders) &&
                        (!notificationVisibility.showInvoices || updated.buyerInvoices) &&
                        (!notificationVisibility.showOcr || updated.ocr) &&
                        (!notificationVisibility.showMessages || updated.messages);
                      updateNotificationToggles({ ...updated, allNotifications: allOn });
                    }}
                    size="small"
                    className={'!w-[54px]'}
                    disabled={isUpdatingNotificationTypes}
                  />
                </div>
              )}

              <div className="flex justify-start pt-2">
                <PopUp
                  content={
                    isNotificationEmailEditing ? (
                      <div className="m-1 max-w-[300px]">
                        <PopUpContentWrapper
                          type="Information"
                          contents={[t('updateButtonDisabledEmailEditing')]}
                        />
                      </div>
                    ) : !hasNotificationTogglesDirty && !isUpdatingNotificationTypes ? (
                      <div className="m-1 max-w-[300px]">
                        <PopUpContentWrapper
                          type="Information"
                          contents={[t('updateButtonDisabledNoChanges')]}
                        />
                      </div>
                    ) : null
                  }
                  popupAlign={{ horizontal: 'center', vertical: 'top' }}
                  anchorAlign={{ horizontal: 'center', vertical: 'bottom' }}
                >
                  <span className="inline-flex">
                    <Button
                      type="button"
                      color="secondary"
                      size="md"
                      disabled={
                        !hasNotificationTogglesDirty ||
                        isUpdatingNotificationTypes ||
                        isNotificationEmailEditing
                      }
                      onClick={() => saveNotificationToggles()}
                      className="w-auto"
                    >
                      {t('Update')}
                    </Button>
                  </span>
                </PopUp>
              </div>
            </div>
          )}
        </div>
      </div>
      <EmailNotificationsConfirmDialog
        open={isUnsavedTogglesDialogOpen}
        onSaveAndContinue={onSaveAndContinueNotificationAddress}
        onDiscardChanges={onDiscardNotificationAddressChanges}
        onCancel={closeUnsavedTogglesDialog}
      />
      <Tooltip
        id={NOTIFICATION_EMAIL_TOOLTIP_ID}
        style={notificationEmailTooltipStyle}
        place="bottom"
      />
    </>
  );
};

export default SystemNotifications;
